import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  type ComponentRef,
  type Injector,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ListStateRegistry } from '@modules/common/components/list/list-state-registry.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateService } from '@modules/settings/services/state.service';
import { listStateViewSettings, type ViewSettingsSource, type ViewSettingsState } from './view-settings';
import { VIEW_SETTINGS_LISTS, type ViewSettingsListId } from './view-settings-lists';
import { ViewSettingsHostComponent } from './view-settings-host.component';

/** The rail row key of the Flagged view, the same in every app. */
const FLAGGED_ROW = 'flagged';

export interface ViewSettingsRequest {
  /** The list the clicked rail row opens. */
  readonly list: ViewSettingsListId;
  /** The row's key (the app's virtual folder): Mail's sort keys and Unread filter depend on it. */
  readonly row: string;
  /** The rail row the menu belongs to: the card opens beside it, and the row is shown first if it is not the open one. */
  readonly origin: HTMLElement;
  /** The menu's injector: the search service is provided below the root, so the not-on-screen fallback asks it. */
  readonly injector: Injector;
}

function isViewSettingsState(value: unknown): value is ViewSettingsState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'notifyStateChanged' in value &&
    typeof value.notifyStateChanged === 'function' &&
    'initialSort' in value
  );
}

/**
 * Opens the "View settings" card for a rail row. The card is owned here, not by the menu, because the menu
 * closes on the click that opens it.
 */
@Injectable({ providedIn: 'root' })
export class ViewSettingsWindowService {
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);
  private readonly registry = inject(ListStateRegistry);
  private readonly stateService = inject(StateService);
  private current: { host: ComponentRef<ViewSettingsHostComponent>; origin: HTMLElement } | undefined;

  public open(request: ViewSettingsRequest): void {
    this.close();
    const origin = request.origin.closest<HTMLElement>('app-sidebar-item') ?? request.origin;
    this.showRow(origin);
    const host = createComponent(ViewSettingsHostComponent, { environmentInjector: this.environmentInjector });
    host.setInput('origin', origin);
    host.setInput('source', this.sourceFor(request));
    host.instance.applied.subscribe((values) => {
      host.instance.source().apply(values);
      this.close();
    });
    host.instance.closed.subscribe(() => this.close());
    this.document.body.appendChild(host.location.nativeElement);
    this.appRef.attachView(host.hostView);
    this.current = { host, origin };
  }

  public close(): void {
    const current = this.current;
    if (!current) return;
    this.current = undefined;
    this.appRef.detachView(current.host.hostView);
    current.host.destroy();
    if (current.origin.isConnected) current.origin.focus();
  }

  /** The list's own state when its list is on screen; otherwise one saved under the same key, which the list restores. */
  private sourceFor({ list, row, injector }: ViewSettingsRequest): ViewSettingsSource {
    const config = VIEW_SETTINGS_LISTS[list];
    const live = this.registry.find(config.stateKey)?.state;
    const state = isViewSettingsState(live)
      ? live
      : config.detached({ stateService: this.stateService, searchService: injector.get(AdvancedSearchService) });
    return listStateViewSettings(state, {
      noun: config.noun,
      sorts: config.sorts(row),
      withUnread: config.withUnread?.(row),
      flaggedLocked: config.hasFlaggedRow && row === FLAGGED_ROW,
    });
  }

  /** Opens the row's view as a click on it does, unless it is the one already open. */
  private showRow(origin: HTMLElement): void {
    if (origin.matches('[aria-current]') || origin.querySelector('[aria-current]')) return;
    origin.click();
  }
}
